import { useEffect, useRef } from "react";
import { EditCollateralFormType } from "../../../types/container.type";
import { EditCollateralWrapper } from "./styled";
import { Box, Chip, CircularProgress, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseLegend } from "../../../components/form/legend/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import Cookies from "universal-cookie";
import { retrieveCollateralService } from "../../../utils/api/loanmanagement/collateral/retrieveCollateral";

export const EditCollateral: React.FC<EditCollateralFormType> = ({ loanId, collateralId, formDetails, setFormDetails, error, isLoading, handleSubmit }) => {
    const collateralTypes = ["car", "house"];

    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const convertUrlToFile = async (attachments: string[]): Promise<File[]> => {
        const files: File[] = [];
        for (const url of attachments) {
            try {
                const response = await fetch(url);
                const blob = await response.blob();
                const name = url.split("/collateral_attachment/")[1];
                const file = new File([blob], name, { type: blob.type });
                files.push(file);
            } catch (err) {
                console.error("Failed to fetch attachment:", url, err);
            }
        };
        return files;
    };

    useEffect(() => {
        const fetchCollateral = async () => {
            if (!loanId || !collateralId) return;
            try {
                const collateral = await retrieveCollateralService(TOKEN, loanId, collateralId);
                const attachments = await convertUrlToFile(collateral.attachments);
                setFormDetails((prev) => ({
                    ...prev,
                    name: collateral.name || prev.name,
                    type: collateral.type || prev.type,
                    serialNo: collateral.serialNo || prev.serialNo,
                    estimatedPrice: collateral.estimatedPrice || prev.estimatedPrice,
                    attachments: attachments || prev.attachments,
                    description: collateral.description || prev.description,
                }))
            } catch (err) {
                console.error("Failed to fetch collateral:", err);
            }
        };
        fetchCollateral();
    }, [TOKEN, setFormDetails, loanId, collateralId]);

    const handleChange = (e: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, type, value } = e.target as HTMLInputElement;
        if (type === 'file') {
            const input = e.target as HTMLInputElement;
            const fileArray = input.files ? Array.from(input.files) : [];
            setFormDetails(prev => ({
                ...prev,
                [name]: [...prev[name as keyof typeof prev] as File[], ...fileArray]
            }));
        } else {
            setFormDetails(prev => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleDeleteAttachment = (fileToDelete: File) => {
        setFormDetails((prev) => {
            const updated = prev.attachments.filter(file => file !== fileToDelete);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            };
            return {
                ...prev,
                attachments: updated
            };
        });
    };

    return (
        <EditCollateralWrapper
            onSubmit={handleSubmit}
        >
            <BaseLegend
                fontweight={500}
                fontsize={"18"}
                color="var(--dark-color)"
            >
                Enter required Information to edit collateral
            </BaseLegend>
            <Grid2
                container
                component={"div"}
                spacing={"var(--flex-gap)"}
                className="fieldset-grid-wrapper"
            >
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Name
                        </BaseLabel>
                        <BaseInput
                            required
                            name="name"
                            value={formDetails.name}
                            placeholder="Enter First Name"
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Collateral Type
                        </BaseLabel>
                        <BaseSelect
                            required
                            name="type"
                            fontweight={400}
                            value={formDetails.type}
                            colour="var(--semi-gray-color)"
                            onChange={(e) => handleChange(e)}
                        >
                            <BaseOption
                                value={" "}
                            >
                                Select Type
                            </BaseOption>
                            {collateralTypes?.map((type: string, index: number) => {
                                return (
                                    <BaseOption
                                        key={index}
                                        value={type}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </BaseOption>
                                )
                            })}
                        </BaseSelect>
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Serial Number
                        </BaseLabel>
                        <BaseInput
                            required
                            readOnly
                            name="serialNo"
                            value={formDetails.serialNo.toString().padStart(3, '0')}
                            placeholder="Serial No."
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Estimated Price
                        </BaseLabel>
                        <BaseInput
                            required
                            name="estimatedPrice"
                            value={formDetails.estimatedPrice}
                            placeholder="Enter Estimated Price"
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Description
                        </BaseLabel>
                        <BaseInput
                            required
                            multiline
                            minRows={4}
                            name="description"
                            value={formDetails.description}
                            placeholder="Add any additional details or notes."
                            onChange={(e) => handleChange(e)}
                        />
                    </BaseFieldSet>
                </Grid2>
                <Grid2
                    size={{ mobile: 12, tablet: 6 }}
                >
                    <BaseFieldSet>
                        <BaseLabel
                            colour="var(--dark-color)"
                        >
                            Attachments
                        </BaseLabel>
                        <BaseInput
                            type="file"
                            name="attachments"
                            inputRef={fileInputRef}
                            inputProps={{
                                accept: ".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx,.rtf,.odt,.ods,.odp",
                                multiple: true,
                            }}
                            onChange={handleChange}
                        />
                        {formDetails.attachments.length > 0 && (
                            <Stack
                                direction={"row"}
                                flexWrap={"wrap"}
                                gap={"calc(var(--flex-gap)/4)"}
                                marginBlockStart={"calc(var(--basic-margin)/4)"}
                            >
                                {formDetails?.attachments?.map((file: File, index) => (
                                    <Chip key={index} label={file.name} onDelete={() => handleDeleteAttachment(file)} />
                                ))}
                            </Stack>
                        )}
                    </BaseFieldSet>
                </Grid2>
            </Grid2>
            {error && <Typography
                fontFamily={"Inter"}
                fontWeight={"600"}
                fontSize={14}
                lineHeight={"normal"}
                color={"var(--text-salmon-pink-color)"}
                whiteSpace={"normal"}
            >
                {error}
            </Typography>}
            <Box
                overflow={"hidden"}
            >
                <BaseButton
                    type="submit"
                    variant="contained"
                    bgcolor="var(--primary-color)"
                    disableElevation
                    sx={{
                        width: { mobile: "100%", miniTablet: "auto" }
                    }}
                >
                    {isLoading ? (
                        <CircularProgress color="inherit" className="loader" />
                    ) : (
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Update Information
                        </Typography>
                    )}
                </BaseButton>
            </Box>
        </EditCollateralWrapper>
    )
};